import { format } from 'd3-format'
import { findIndex, isEmpty, isNil, omitBy, isString } from 'lodash'
import i18n from 'i18next'

import { isKeyOf } from '@ahha/utils/@types/typeChecks'
import { WidgetPreset } from '@/pages/DataCamp/Dashboard/const'

export const getWidgetTitle = (widgetType: number): string => {
  if (widgetType === WidgetPreset.LINE_CHART) {
    return i18n.t('widget_type_lineChart')
  }
  if (widgetType === WidgetPreset.BAR_CHART) {
    return i18n.t('widget_type_barChart')
  }
  if (widgetType === WidgetPreset.PIE_CHART) {
    return i18n.t('widget_type_pieChart')
  }
  if (widgetType === WidgetPreset.TABLE) {
    return i18n.t('common_table')
  }
  if (widgetType === WidgetPreset.IMAGE) {
    return i18n.t('common_image')
  }
  if (widgetType === WidgetPreset.TEXT) {
    return i18n.t('common_text')
  }
  return i18n.t('widget_type_container')
}

export const checkIsOnlyDigits = (value: string) => {
  const regex = /^[0-9]*$/
  return regex.test(value)
}

export const limitDecimalPlaces = (value: string | number, decimalPlace: number) => {
  const stringValue = String(value)
  if (isEmpty(stringValue)) {
    return ''
  }
  if (stringValue.substring(stringValue.indexOf('.')).length > decimalPlace + 1) {
    return Number(stringValue.slice(0, -1))
  }
  return Number(value)
}

export const searchDictionary = (dictionary: Record<string, string>, search: string) => {
  if (isKeyOf(search, dictionary)) {
    return dictionary[search]
  }
  return null
}

export const getTotalPage = (totalCount: number | undefined, itemsPerPage: number) => Math.ceil((totalCount ?? itemsPerPage) / itemsPerPage)

export const omitNilOrEmptyStringFromObject = (obj: { [key: string]: any }) => omitBy(obj, (o) => isNil(o) || o === '')

export const getCheckpointNumber = (weightScores: Record<string, any>[] | undefined, targetWeight: Record<string, any>| undefined | null) => {
  if (!weightScores || !targetWeight) {
    return undefined
  }
  const targetIndex = findIndex(weightScores, targetWeight)
  return `Top ${targetIndex + 1}`
}

export const sanitizeFileName = (fileName: string) => (
  fileName
    .replace(/[<>:"|?*]/g, '_') // Replace forbidden characters (windows 기준)
    .replace(/\//g, '_') // Replace forward slash
    .replace(/\\/g, '_') // Replace backslash
    .replace(/\s+/g, '_') // replace spaces with underscores
    .trim()
)

export const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text)
  } catch (e) {
    // NOTE: http환경에서는 실패시 -> 예전 방식으로 적용
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.top = '0'
    textarea.style.left = '0'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    try {
      document.execCommand('copy')
    } finally {
      document.body.removeChild(textarea)
    }
  }
}

export const humanizeNumber = (value: number, decimalPlaces: number = 3) => {
  if (isString(value)) {
    return value
  }
  const absValue = Math.abs(value)

  // For very small numbers, use scientific notation
  if (absValue < 0.01 && value !== 0) {
    return format(`.${decimalPlaces}e`)(value)
  }

  // For numbers that don't need SI-prefix (< 1000), use fixed decimal places
  if (absValue < 1000) {
    return format(`.${decimalPlaces}~f`)(value)
  }

  // For large numbers, use SI-prefix with significant digits
  return format(`.${decimalPlaces}~s`)(value)
}
